/**
 * API Error Handler Service
 * Centralized service for handling API errors and redirecting to error pages.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { navigateToError } from '../features/shared/pages/ErrorPages';

// Error handling types
export interface ExtendedApiError {
  name: string;
  message: string;
  status: number;
  code: string;
  details?: Record<string, any>;
  timestamp?: string;
  field?: string;
}

export interface ApiErrorHandlerOptions {
  autoRedirect?: boolean; // Automatically redirect to error pages
  showNotification?: boolean; // Display a toast notification
  logError?: boolean; // Log the error to the console
  context?: string; // Additional context for debugging
}

export interface ErrorMetrics {
  errorCount: number;
  lastError?: ExtendedApiError;
  lastErrorTime?: Date;
  errorsByType: Record<string, number>;
  errorsByStatus: Record<number, number>;
}

class ApiErrorHandlerService {
  private metrics: ErrorMetrics = {
    errorCount: 0,
    errorsByType: {},
    errorsByStatus: {},
  };

  private navigate: ((path: string) => void) | null = null;

  /**
   * Initializes the service with a navigation function.
   * @param navigateFunction - Function to handle navigation.
   */
  initialize(navigateFunction: (path: string) => void): void {
    this.navigate = navigateFunction;
  }

  /**
   * Handles an API error with configurable options.
   * @param error - The error to handle.
   * @param options - Configuration for error handling.
   */
  handleError(error: ExtendedApiError | Error | unknown, options: ApiErrorHandlerOptions = {}): void {
    const { autoRedirect = true, showNotification = true, logError = true, context = 'Unknown' } = options;

    const apiError = this.normalizeError(error, context);
    this.updateMetrics(apiError);

    if (logError) {
      this.logError(apiError, context);
    }

    if (showNotification) {
      this.showErrorNotification(apiError);
    }

    if (autoRedirect && this.navigate) {
      this.redirectToErrorPage(apiError);
    }
  }

  /**
   * Normalizes an error into an ExtendedApiError format.
   * @param error - The error to normalize.
   * @param context - Contextual information for the error.
   * @returns Normalized ExtendedApiError.
   */
  private normalizeError(error: unknown, context: string): ExtendedApiError {
    if (this.isExtendedApiError(error)) {
      return error;
    }

    if (error instanceof Error) {
      return {
        name: 'ApiError',
        message: error.message || 'Unknown error',
        status: 500,
        code: 'UNKNOWN_ERROR',
        details: {
          context,
          timestamp: new Date().toISOString(),
          originalError: error.message,
        },
      };
    }

    return {
      name: 'ApiError',
      message: 'Unexpected error',
      status: 500,
      code: 'UNEXPECTED_ERROR',
      details: {
        context,
        timestamp: new Date().toISOString(),
        originalError: String(error),
      },
    };
  }

  /**
   * Checks if an object is an ExtendedApiError.
   * @param error - The object to check.
   * @returns True if the object is an ExtendedApiError.
   */
  private isExtendedApiError(error: unknown): error is ExtendedApiError {
    return (
      typeof error === 'object' &&
      error !== null &&
      'name' in error &&
      'message' in error &&
      'status' in error &&
      'code' in error
    );
  }

  /**
   * Updates error metrics.
   * @param error - The error to record in metrics.
   */
  private updateMetrics(error: ExtendedApiError): void {
    this.metrics.errorCount++;
    this.metrics.lastError = error;
    this.metrics.lastErrorTime = new Date();

    if (error.code) {
      this.metrics.errorsByType[error.code] = (this.metrics.errorsByType[error.code] || 0) + 1;
    }

    if (error.status) {
      this.metrics.errorsByStatus[error.status] = (this.metrics.errorsByStatus[error.status] || 0) + 1;
    }
  }

  /**
   * Logs an error to the console with detailed information.
   * @param error - The error to log.
   * @param context - Contextual information for the error.
   */
  private logError(error: ExtendedApiError, context: string): void {
    const errorInfo = {
      context,
      error: {
        name: error.name,
        message: error.message,
        status: error.status,
        code: error.code,
      },
      details: error.details,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
    };

    console.group(`API Error in ${context}`);
    console.error('Error Details:', errorInfo);
    console.error('Stack Trace:', error);
    console.groupEnd();

    if (import.meta.env.DEV) {
      console.info('Error Metrics:', this.getMetrics());
    }
  }

  /**
   * Displays an error notification.
   * @param error - The error to display in the notification.
   */
  private showErrorNotification(error: ExtendedApiError): void {
    console.warn(`Notification: ${error.message}`);
  }

  /**
   * Redirects to the appropriate error page based on error status.
   * @param error - The error triggering the redirection.
   */
  private redirectToErrorPage(error: ExtendedApiError): void {
    if (!this.navigate) {
      console.warn('Navigation function not initialized');
      return;
    }

    // Ne pas rediriger pour les erreurs 404, laisser le composant gérer
    if (error.status === 404) {
      console.warn('404 Not Found détecté - pas de redirection pour laisser le composant gérer le fallback');
      return;
    }

    let redirectPath: string;

    switch (error.status) {
      case 400:
        redirectPath = navigateToError.badRequest();
        break;
      case 401:
        redirectPath = navigateToError.unauthorized();
        break;
      case 403:
        redirectPath = navigateToError.forbidden();
        break;
      case 408:
        redirectPath = navigateToError.timeout();
        break;
      case 429:
        // Ne PAS rediriger automatiquement pour éviter les boucles
        // Les erreurs 429 sont gérées par le cache et les mécanismes de retry
        console.warn('429 Too Many Requests détecté - pas de redirection pour éviter les boucles');
        return; // Sortir sans redirection
        break;
      case 500:
        redirectPath = navigateToError.serverError();
        break;
      case 502:
        redirectPath = navigateToError.badGateway();
        break;
      case 503:
        redirectPath = navigateToError.serviceUnavailable();
        break;
      default:
        redirectPath = navigateToError.generic(error.status || 500);
    }

    console.log(`Redirecting to error page: ${redirectPath}`);
    this.navigate(redirectPath);
  }

  /**
   * Returns the current error metrics.
   * @returns A copy of the error metrics.
   */
  getMetrics(): ErrorMetrics {
    return { ...this.metrics };
  }

  /**
   * Resets error metrics to their initial state.
   */
  resetMetrics(): void {
    this.metrics = {
      errorCount: 0,
      errorsByType: {},
      errorsByStatus: {},
    };
  }

  /**
   * Checks if the API is healthy based on recent errors.
   * @returns True if the API is considered healthy.
   */
  isApiHealthy(): boolean {
    const hasRecentServerErrors = Object.keys(this.metrics.errorsByStatus).some(
      (status) => parseInt(status) >= 500 && this.metrics.errorsByStatus[parseInt(status)] > 0
    );

    return this.metrics.errorCount < 5 && !hasRecentServerErrors;
  }

  /**
   * Generates a formatted error report.
   * @returns A string containing the error report.
   */
  getErrorReport(): string {
    const { errorCount, lastError, lastErrorTime, errorsByType, errorsByStatus } = this.metrics;

    return `
API Error Report
================
Total Errors: ${errorCount}
Last Error: ${lastError?.message || 'None'}
Time: ${lastErrorTime?.toLocaleString() || 'N/A'}

Errors by Type:
${Object.entries(errorsByType)
  .map(([type, count]) => `  ${type}: ${count}`)
  .join('\n')}

Errors by HTTP Status:
${Object.entries(errorsByStatus)
  .map(([status, count]) => `  ${status}: ${count}`)
  .join('\n')}

API Status: ${this.isApiHealthy() ? 'Healthy' : 'Problematic'}
    `;
  }
}

/**
 * React hook to use the API Error Handler service.
 * @returns An object with methods to handle errors and retrieve metrics.
 */
export function useApiErrorHandler() {
  const navigate = useNavigate();

  React.useEffect(() => {
    apiErrorHandler.initialize(navigate);
  }, [navigate]);

  return {
    handleError: (error: unknown, options?: ApiErrorHandlerOptions) =>
      apiErrorHandler.handleError(error, options),
    getMetrics: () => apiErrorHandler.getMetrics(),
    resetMetrics: () => apiErrorHandler.resetMetrics(),
    isApiHealthy: () => apiErrorHandler.isApiHealthy(),
    getErrorReport: () => apiErrorHandler.getErrorReport(),
  };
}

// Singleton instance - Lazy initialization
let _apiErrorHandler: ApiErrorHandlerService | null = null;

export const apiErrorHandler = (() => {
  if (!_apiErrorHandler) {
    _apiErrorHandler = new ApiErrorHandlerService();
  }
  return _apiErrorHandler;
})();

export default apiErrorHandler;

// Auto-initialize in development mode
if (import.meta.env.DEV) {
  console.log('API Error Handler Service initialized in development mode');
}